import { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import {
  usePanelStackStore,
  type StackedPanel,
  type StackedPanelInput,
  type GlobalPanelEntityType,
  ENTITY_URL_PARAMS,
} from '@/store/panel-stack-store';

/**
 * Parse panel stack from URL search params.
 *
 * URL format:
 * - panelStack=contact,company (comma-separated order, bottom to top)
 * - contactId=xxx
 * - companyId=yyy
 */
function parseStackFromSearchObject(search: Record<string, unknown>): StackedPanelInput[] {
  const panelStackParam = search.panelStack as string | undefined;
  if (!panelStackParam) return [];

  const types = String(panelStackParam).split(',').filter(Boolean) as GlobalPanelEntityType[];
  const stack: StackedPanelInput[] = [];

  for (const type of types) {
    const paramName = ENTITY_URL_PARAMS[type];
    if (paramName) {
      const id = search[paramName] as string | undefined;
      if (id) {
        stack.push({ type, id });
      }
    }
  }

  return stack;
}

/**
 * Serialize panel stack to search object.
 */
function serializeStackToSearchObject(
  stack: StackedPanel[],
  currentSearch: Record<string, unknown>,
): Record<string, unknown> {
  const params = { ...currentSearch };

  // Remove all panel-related params first
  delete params.panelStack;
  Object.values(ENTITY_URL_PARAMS).forEach((param) => delete params[param]);

  if (stack.length === 0) {
    return params;
  }

  // Add panelStack param (order)
  params.panelStack = stack.map((p) => p.type).join(',');

  // Add entity ID params
  for (const panel of stack) {
    const paramName = ENTITY_URL_PARAMS[panel.type];
    if (paramName) {
      params[paramName] = panel.id;
    }
  }

  return params;
}

/**
 * Serialize stack entries for equality comparison.
 */
function serializeStackForComparison(panels: StackedPanelInput[]): string {
  return panels.map((p) => `${p.type}:${p.id}`).join(',');
}

/**
 * Hook that synchronizes panel stack state with URL.
 *
 * - On mount: parses URL and hydrates store
 * - On store change: updates URL
 * - Handles manual URL edits gracefully
 */
export function usePanelStackUrl() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  // Get raw search params as a record
  const search = location.search as unknown as Record<string, unknown>;

  const {
    stack,
    setStackFromUrl,
    pushPanel,
    popPanel,
    closeToLevel,
    clearAll,
    navigateWithinPanel,
  } = usePanelStackStore();

  // Track whether we're currently updating the URL to avoid loops
  const isUpdatingUrl = useRef(false);
  // Track whether we're currently syncing from URL to store to avoid loops
  const isSyncingFromUrl = useRef(false);
  // Track if we've done initial hydration
  const hasHydrated = useRef(false);
  // Track last known URL stack to detect external URL changes
  const lastUrlStackRef = useRef<string>('');
  // Track last known store stack to detect store changes
  const lastStoreStackRef = useRef<string>('');
  // Track pending timeouts for cleanup on unmount
  const pendingTimeouts = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  // Helper to schedule a timeout that is tracked for cleanup
  const scheduleTimeout = useCallback((fn: () => void, delay: number) => {
    const id = setTimeout(() => {
      pendingTimeouts.current.delete(id);
      fn();
    }, delay);
    pendingTimeouts.current.add(id);
  }, []);

  // Hydrate store from URL on mount
  useEffect(() => {
    if (hasHydrated.current) return;
    hasHydrated.current = true;

    const urlStack = parseStackFromSearchObject(search);
    const urlStackStr = serializeStackForComparison(urlStack);
    lastUrlStackRef.current = urlStackStr;
    lastStoreStackRef.current = urlStackStr;

    if (urlStack.length > 0) {
      setStackFromUrl(urlStack);
    }
  }, [search, setStackFromUrl]);

  // Sync URL changes to store (external navigation like clicking pinned items)
  useEffect(() => {
    if (!hasHydrated.current) return;
    if (isUpdatingUrl.current) return;

    const urlStack = parseStackFromSearchObject(search);
    const urlStackStr = serializeStackForComparison(urlStack);

    // Check if URL changed externally (not from our own updates)
    if (urlStackStr !== lastUrlStackRef.current) {
      lastUrlStackRef.current = urlStackStr;
      // Set flag to prevent store→URL sync from running with stale store data
      isSyncingFromUrl.current = true;
      setStackFromUrl(urlStack);
      // Reset flag after React has processed the state update
      scheduleTimeout(() => {
        lastStoreStackRef.current = urlStackStr;
        isSyncingFromUrl.current = false;
      }, 0);
    }
  }, [search, setStackFromUrl, scheduleTimeout]);

  // Sync store changes to URL (programmatic openPanel/closePanel calls)
  useEffect(() => {
    if (!hasHydrated.current) return;
    if (isUpdatingUrl.current) return;
    if (isSyncingFromUrl.current) return;

    const storeStackStr = serializeStackForComparison(stack);

    // Check if store changed (not from URL sync)
    if (storeStackStr !== lastStoreStackRef.current) {
      lastStoreStackRef.current = storeStackStr;
      lastUrlStackRef.current = storeStackStr;

      isUpdatingUrl.current = true;
      const newSearch = serializeStackToSearchObject(stack, search);
      navigate({
        to: pathname,
        search: newSearch,
        replace: true,
      });
      // Reset flag after navigation completes
      scheduleTimeout(() => {
        isUpdatingUrl.current = false;
      }, 50);
    }
  }, [stack, pathname, navigate, search, scheduleTimeout]);

  // Cleanup all pending timeouts on unmount
  useEffect(() => {
    const timeouts = pendingTimeouts.current;
    return () => {
      timeouts.forEach(clearTimeout);
      timeouts.clear();
    };
  }, []);

  return {
    stack,
    openPanel: pushPanel,
    closeTopPanel: popPanel,
    closePanelAt: closeToLevel,
    closeAllPanels: clearAll,
    navigateInPanel: navigateWithinPanel,
  };
}
